import { useMemo, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiUrl } from '../../lib/api-base'
import {
  REGISTER_COUNTRIES,
  applyCountryDialToPhone,
  dialForCountry,
} from '../../client/registerCountries'
import { portalLinks } from '../portal-links'

type Props = {
  className?: string
}

/**
 * Compact Create Account panel for ads landings.
 * Uses the same /api/client/register contract as the app register API.
 */
export function AdsRegisterPanel({ className = '' }: Props) {
  const [searchParams] = useSearchParams()
  const ibCode = (searchParams.get('ib') || searchParams.get('ref') || '').trim().toUpperCase()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [country, setCountry] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [devToken, setDevToken] = useState('')

  const countryOptions = useMemo(() => REGISTER_COUNTRIES, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const res = await fetch(apiUrl('/api/client/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
          country: country.trim(),
          ib_code: ibCode || '',
        }),
      })
      const data = (await res.json().catch(() => null)) as {
        success?: boolean
        email?: string
        devVerificationToken?: string
        error?: { message?: string }
      } | null
      if (!res.ok || !data?.success) {
        throw new Error(data?.error?.message || 'Registration failed. Please try again.')
      }
      setDone(true)
      if (data.devVerificationToken) setDevToken(data.devVerificationToken)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <aside className={`ads-reg ads-reg--done ${className}`} aria-label="Create account">
        <h2 className="ads-reg__title">Check your email</h2>
        <p className="ads-reg__lead">
          Account created. Open the verification link we sent to continue.
        </p>
        {devToken ? (
          <p className="ads-reg__note">
            Local/dev token ready —{' '}
            <a
              href={`${portalLinks.home.replace(/\/app$/, '')}/app/verify-email?token=${encodeURIComponent(devToken)}`}
            >
              verify now
            </a>
          </p>
        ) : null}
        <a className="ads-reg__submit" href={portalLinks.login}>
          Continue to sign in
        </a>
      </aside>
    )
  }

  return (
    <aside className={`ads-reg ${className}`} aria-label="Create account">
      <h2 className="ads-reg__title">Create Account</h2>
      <p className="ads-reg__lead">Start in under a minute — then open Live when you are ready.</p>
      {ibCode ? <p className="ads-reg__ib">Referral: {ibCode}</p> : null}

      <form className="ads-reg__form" onSubmit={(e) => void onSubmit(e)}>
        <div className="ads-reg__row">
          <label>
            First name
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
            />
          </label>
          <label>
            Last name
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
          </label>
        </div>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </label>
        <label>
          Country
          <select
            value={country}
            className={country ? '' : 'is-placeholder'}
            onChange={(e) => {
              const next = e.target.value
              setCountry(next)
              const dial = dialForCountry(next)
              if (dial) setPhone((prev) => applyCountryDialToPhone(prev, dial))
            }}
            required
          >
            <option value="" disabled>
              Select country
            </option>
            {countryOptions.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Phone
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            required
          />
        </label>

        {error ? (
          <p className="ads-reg__error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="ads-reg__submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create Account'}
        </button>
      </form>

      <p className="ads-reg__foot">
        Already have an account? <a href={portalLinks.login}>Sign in</a>
      </p>
      <p className="ads-reg__risk">Trading involves risk. No guaranteed returns.</p>
    </aside>
  )
}
